import { DEAL_TYPE, FEE_TYPE, INSTALLMENT_TYPE } from 'constants/dealConstants';
import Decimal from 'decimal.js';
import moment from 'moment';

const FIELD_LABEL_FALLBACKS = {
  purchasePrices: 'Purchase Price',
  purchasePriceDocument: 'Purchase Price Document (PDF)',
  'feeSettings.feeType': 'Fee Type',
  'feeSettings.feePct': 'Fee',
  'feeSettings.irrCapPct': 'IRR Cap',
  pctFromRevenueShare: 'Revenue Margin',
  revenueTotalType: 'Revenue Type',
  accelerationPct: 'Acceleration %',
  accelerationRevenueTotalType: 'Acceleration Revenue Type',
  minRunrate: 'Min. Runrate',
};

function getFieldLabel(fields, name) {
  const field = (fields || []).find((item) => item.name === name);
  if (field && typeof field.label === 'string') {
    return field.label.replace(/\s*\*$/, '').trim();
  }
  return FIELD_LABEL_FALLBACKS[name] || name;
}

function getValueByName(values, name) {
  if (!name.includes('.')) {
    return values?.[name];
  }
  const [parentName, childName] = name.split('.');
  return values?.[parentName]?.[childName];
}

function hasFilledValue(value) {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return value !== '' && value !== null && value !== undefined;
}

function arePurchasePricesValid(purchasePrices, installmentType) {
  if (!Array.isArray(purchasePrices) || purchasePrices.length === 0) {
    return false;
  }
  return purchasePrices.every(
    (purchasePrice) =>
      !isNaN(purchasePrice.amount) &&
      purchasePrice.amount &&
      (purchasePrice.bankAccountId ||
        installmentType === INSTALLMENT_TYPE.PREVIEW)
  );
}

function getAmortizationMissingReasons({
  amortizationData,
  formValues,
  dealType,
}) {
  const missing = [];
  const { amortization, amountSold } = amortizationData || {};

  if (!amortization || amortization.length === 0) {
    missing.push('Build the repayment schedule');
    return missing;
  }

  const isTrueUp = formValues.isTrueUp;

  const hasRowValues = amortization.every((repayment) => {
    const hasValue = isTrueUp
      ? !isNaN(repayment.trueUpPct)
      : repayment.minAmount;
    return repayment.date && hasValue;
  });
  if (!hasRowValues) {
    missing.push(
      isTrueUp
        ? 'Fill True Up % for every schedule row'
        : 'Fill minimum amount for every schedule row'
    );
  }

  if (dealType === DEAL_TYPE.ROAS) {
    const revenueDatesValid = amortization.every((repayment) => {
      const { revenueStartDate, revenueEndDate } = repayment;
      return !moment(revenueStartDate).isAfter(revenueEndDate);
    });
    if (!revenueDatesValid) {
      missing.push('Fix revenue start/end dates on the schedule');
    }
  }

  if (isTrueUp) {
    if (amortization[amortization.length - 1].trueUpPct !== 1) {
      missing.push('Last schedule True Up % must be 100%');
    }
    let previousTrueUpPct = amortization[0].trueUpPct;
    const increasing = amortization.every((repayment, index) => {
      if (index === 0) {
        return true;
      }
      if (repayment.trueUpPct > 0) {
        const result = repayment.trueUpPct > previousTrueUpPct;
        previousTrueUpPct = repayment.trueUpPct;
        return result;
      }
      return true;
    });
    if (!increasing) {
      missing.push('True Up % values must increase across the schedule');
    }
  } else if (amortization.length > 0) {
    const sumOfRepayments = Decimal.sum(
      ...amortization.map((repayment) => {
        const raw =
          repayment.actualPaidAmount ||
          repayment.amountToPay ||
          repayment.minAmount ||
          0;
        try {
          return new Decimal(raw);
        } catch {
          return new Decimal(0);
        }
      })
    );
    if (!sumOfRepayments.eq(amountSold || 0)) {
      missing.push('Schedule repayment total must equal purchase price');
    }
  }

  return missing;
}

/**
 * Returns human-readable reasons the installment Apply button stays disabled.
 */
export function getInstallmentApplyMissingReasons({
  formValues,
  fields,
  purchasePriceFile,
  amortizationData,
  dealType,
  isPageDisabled,
}) {
  if (isPageDisabled) {
    return ['This installment cannot be edited'];
  }

  const missing = [];
  const values = formValues || {};

  if (!purchasePriceFile && !values.contractFile) {
    missing.push(getFieldLabel(fields, 'purchasePriceDocument'));
  }

  const requiredFields = (fields || []).filter(
    (field) =>
      field.required &&
      !field.isHidden &&
      field.name !== 'purchasePriceDocument'
  );

  requiredFields.forEach((field) => {
    if (field.name === 'purchasePrices') {
      if (!arePurchasePricesValid(values.purchasePrices, values.installmentType)) {
        missing.push(
          values.installmentType === INSTALLMENT_TYPE.FUNDED
            ? 'Purchase Price and bank account'
            : 'Purchase Price'
        );
      }
      return;
    }

    if (field.name.startsWith('feeSettings.')) {
      const feeType = values.feeSettings?.feeType;
      if (field.name === 'feeSettings.feePct' && feeType !== FEE_TYPE.FIXED) {
        return;
      }
      if (field.name === 'feeSettings.irrCapPct' && feeType !== FEE_TYPE.IRR_CAP && feeType !== FEE_TYPE.IRR_CAP_DAILY) {
        return;
      }
    }

    if (field.name === 'accelerationPct' || field.name === 'accelerationRevenueTotalType') {
      if (values.accelerationEnabled !== true) {
        return;
      }
    }

    const value = getValueByName(values, field.name);
    if (!hasFilledValue(value)) {
      missing.push(getFieldLabel(fields, field.name));
    }
  });

  if (
    values.firstRepaymentDate &&
    values.transferDate &&
    !moment(values.firstRepaymentDate).isAfter(moment(values.transferDate))
  ) {
    missing.push('First repayment date must be after transfer date');
  }

  if (values.accelerationEnabled === true) {
    if (!hasFilledValue(values.accelerationPct)) {
      missing.push(getFieldLabel(fields, 'accelerationPct'));
    }
    if (!hasFilledValue(values.accelerationRevenueTotalType)) {
      missing.push(getFieldLabel(fields, 'accelerationRevenueTotalType'));
    }
  }

  missing.push(
    ...getAmortizationMissingReasons({
      amortizationData,
      formValues: values,
      dealType,
    })
  );

  return [...new Set(missing)];
}
